import { useQuery } from '@tanstack/react-query'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { taskScanApi } from '@/lib/api/taskScan'
import { formatDateTime } from '@/lib/utils'
import { formatDuplicateConflictMessage } from '@/lib/utils/scanDuplicate'

interface TaskDuplicateLogPanelProps {
  taskUuid: string
}

export function TaskDuplicateLogPanel({ taskUuid }: TaskDuplicateLogPanelProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['task-scan-duplicates', taskUuid],
    queryFn: async () => {
      const res = await taskScanApi.listDuplicates(taskUuid, { limit: 100, offset: 0 })
      return res.data
    },
  })

  const rows = data?.data ?? []

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <h2 className="text-lg font-semibold mb-1">Дубли при сканировании</h2>
      <p className="text-sm text-gray-500 mb-4">Журнал повторных сканов КМ по заданию.</p>

      {isLoading ? (
        <LoadingSpinner className="py-6" />
      ) : isError ? (
        <p className="text-sm text-gray-500">Не удалось загрузить журнал дублей.</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">Дублей пока нет.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-3 font-medium">Время</th>
                <th className="py-2 pr-3 font-medium">Воркер</th>
                <th className="py-2 pr-3 font-medium">Задание</th>
                <th className="py-2 pr-3 font-medium">Паллета</th>
                <th className="py-2 pr-3 font-medium">Коробка</th>
                <th className="py-2 pr-3 font-medium">Код</th>
                <th className="py-2 font-medium">Оригинал</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const origin = formatDuplicateConflictMessage({
                  raw: row.raw || row.canonical,
                  task_id: row.task_id,
                  dup_box_num: row.dup_box_num ?? row.box_num,
                  dup_pallet_num: row.dup_pallet_num,
                })
                return (
                  <tr key={`${row.created_at}-${row.canonical}-${row.box_num}`} className="border-b border-gray-100 bg-red-50/60">
                    <td className="py-2 pr-3 whitespace-nowrap">{formatDateTime(row.created_at)}</td>
                    <td className="py-2 pr-3">{row.worker_name || '—'}</td>
                    <td className="py-2 pr-3">{row.task_id || '—'}</td>
                    <td className="py-2 pr-3">{row.pallet_num ? `№${row.pallet_num}` : '—'}</td>
                    <td className="py-2 pr-3">№{row.box_num}</td>
                    <td className="py-2 pr-3 font-mono text-xs max-w-xs truncate" title={row.raw || row.canonical}>
                      {row.raw || row.canonical}
                    </td>
                    <td className="py-2 text-red-700">{origin}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
